import { useQuery } from '@tanstack/react-query';
import { Users } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { api, useAction, useProject } from '../../api/hooks';
import { useIsAdmin } from '../../api/session';
import type { Member, UserSummary } from '../../api/types';
import { useCrumbs } from '../../layout/crumbs';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { EmptyState, ErrorAlert, PageUnavailable, QueryView } from '../../ui/feedback';
import { Field, Select } from '../../ui/forms';
import { ConfirmDialog } from '../../ui/Modal';
import { Card, PageHeader, usePageTitle } from '../../ui/misc';
import { Timestamp } from '../../ui/Timestamp';
import { SettingsNav } from './SettingsNav';

/** IDN-03/04: explicit membership; removal ends access on the next check and keeps contributions. */
export function MembersPage() {
  const { key } = useParams();
  const admin = useIsAdmin();
  const project = useProject(key);
  usePageTitle(project.data ? `${project.data.name} · Members` : null);
  useCrumbs([
    { label: project.data?.name ?? key ?? '', to: `/projects/${key}` },
    { label: 'Settings', to: `/projects/${key}/settings` },
    { label: 'Members' },
  ]);
  const members = useQuery({
    queryKey: ['members', key],
    queryFn: () => api.get<Member[]>(`/projects/${key}/members`),
    enabled: admin,
  });
  const users = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<UserSummary[]>('/admin/users'),
    enabled: admin,
  });
  const [adding, setAdding] = useState('');
  const [removing, setRemoving] = useState<Member | null>(null);
  const add = useAction(() => api.post(`/projects/${key}/members`, { userId: adding }), {
    success: 'Member added',
    invalidate: [['members', key], ['project', key], ['users']],
    onSuccess: () => setAdding(''),
  });
  const remove = useAction((m: Member) => api.del(`/projects/${key}/members/${m.id}`), {
    success: 'Member removed',
    invalidate: [['members', key], ['project', key], ['users']],
    onSuccess: () => setRemoving(null),
  });
  if (!admin) return <PageUnavailable />;
  return (
    <div className="page">
      <PageHeader
        title="Members"
        subtitle="Members can view this project, submit requests and discuss them. Admins can access every project."
      />
      <SettingsNav projectKey={key!} />
      <QueryView query={members}>
        {(list) => {
          const candidates = (users.data ?? []).filter((u) => u.active && !list.some((m) => m.id === u.id));
          return (
            <>
              <Card title="Add member" tight>
                <form
                  className="row nowrap"
                  style={{ alignItems: 'flex-end' }}
                  onSubmit={(e) => {
                    e.preventDefault();
                    add.mutate(undefined);
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <Field label="Account">
                      {(p) => (
                        <Select
                          {...p}
                          value={adding}
                          onChange={(e) => setAdding(e.target.value)}
                          disabled={project.data?.archived}
                        >
                          <option value="">Choose an account…</option>
                          {candidates.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.displayName}
                              {u.email ? ` (${u.email})` : ''}
                            </option>
                          ))}
                        </Select>
                      )}
                    </Field>
                  </div>
                  <Button type="submit" disabled={!adding} loading={add.isPending}>
                    Add member
                  </Button>
                </form>
                <span className="small muted">People who are not listed need an invitation first.</span>
                <ErrorAlert error={add.error} />
              </Card>
              {list.length === 0 ? (
                <EmptyState icon={Users} title="No members yet">
                  Add people who should view and submit work.
                </EmptyState>
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Role</th>
                        <th>State</th>
                        <th className="num">Open assignments</th>
                        <th>Added</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {list.map((m) => (
                        <tr key={m.id}>
                          <td>
                            <div className="primary-cell">{m.displayName}</div>
                            <div className="small muted">{m.email}</div>
                          </td>
                          <td>{m.role === 'admin' ? 'Admin' : 'Member'}</td>
                          <td>{m.active ? <Badge tone="success">Active</Badge> : <Badge>Inactive</Badge>}</td>
                          <td className="num">{m.openAssignments}</td>
                          <td className="small">
                            <Timestamp value={m.addedAt} dateOnly />
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <Button variant="ghost" size="sm" onClick={() => setRemoving(m)}>
                              Remove
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {removing && (
                <ConfirmDialog
                  title={`Remove ${removing.displayName}?`}
                  confirmLabel="Yes, remove"
                  variant="danger"
                  onClose={() => setRemoving(null)}
                  onConfirm={() => remove.mutate(removing)}
                  pending={remove.isPending}
                  error={remove.error}
                >
                  <p>
                    Access to this project's board, search, tickets, files, and notifications ends on their
                    next request. Their tickets, comments, and approvals remain attributed to them.
                    {removing.openAssignments > 0 &&
                      ` They still have ${removing.openAssignments} open assignment(s); reassign them from the board.`}
                  </p>
                </ConfirmDialog>
              )}
            </>
          );
        }}
      </QueryView>
    </div>
  );
}
